import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Calendar, Clock, Users, Mail, Phone, Check, X, Trash2, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLLING_INTERVAL = 30000; // 30 seconds

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const previousBookingsCount = useRef(0);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    verifyToken();
    fetchBookings();
    
    // Setup polling every 30 seconds
    const intervalId = setInterval(() => {
      fetchBookings(true); // silent refresh
    }, POLLING_INTERVAL);
    
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.clear();
      toast.error('Sessione scaduta. Effettua nuovamente il login.');
      navigate('/admin/login');
    }
  };

  const fetchBookings = async (silent = false) => {
    if (!silent) setIsLoading(true);
    const token = localStorage.getItem('admin_token');

    try {
      const response = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newBookings = response.data;
      
      // Detect new bookings on silent refresh
      if (silent && !isFirstLoad.current) {
        const newCount = newBookings.length;
        const oldCount = previousBookingsCount.current;
        if (newCount > oldCount) {
          const diff = newCount - oldCount;
          toast.success(`${diff} nuova prenotazione${diff > 1 ? 'i' : ''} ricevuta${diff > 1 ? 'e' : ''}!`, {
            duration: 5000,
          });
        }
      }
      
      previousBookingsCount.current = newBookings.length;
      isFirstLoad.current = false;
      setBookings(newBookings);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching bookings:', error);
      if (!silent) toast.error('Errore nel caricamento delle prenotazioni.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    const token = localStorage.getItem('admin_token');
    let reason = null;
    
    // Ask for cancellation reason
    if (status === 'cancelled') {
      reason = window.prompt('Motivo della cancellazione (opzionale) — verrà inviato al cliente via email:', '');
      if (reason === null) return; // User clicked cancel
    }

    try {
      await axios.patch(
        `${API}/bookings/${bookingId}`,
        { status, reason: reason || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (status === 'confirmed') {
        toast.success('Prenotazione confermata! Email inviata al cliente.');
      } else {
        toast.success('Prenotazione cancellata. Email di notifica inviata al cliente.');
      }
      
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error("Errore nell'aggiornamento della prenotazione.");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm('Sei sicuro di voler eliminare definitivamente questa prenotazione? Il cliente NON riceverà notifiche.')) return;

    const token = localStorage.getItem('admin_token');

    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Prenotazione eliminata!');
      fetchBookings();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error("Errore nell'eliminazione della prenotazione.");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success('Logout effettuato.');
    navigate('/admin/login');
  };

  const filteredBookings = bookings.filter((booking) => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-500';
      case 'cancelled':
        return 'text-red-500';
      default:
        return 'text-yellow-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Confermata';
      case 'cancelled':
        return 'Cancellata';
      default:
        return 'In Attesa';
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-[#222222]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#1A1A1A] border-b border-[#FFDA00]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1
              className="text-2xl md:text-3xl font-bold"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              <span className="text-[#FFDA00]">Rovèscio</span> <span className="text-white">Admin</span>
            </h1>
            <p className="text-gray-400 text-sm">Gestione Prenotazioni</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-xs text-gray-400 flex items-center gap-2" data-testid="last-refresh">
              <RefreshCw size={14} className="text-[#FFDA00]" />
              <span>Ultimo refresh: {formatTime(lastRefresh)}</span>
            </div>
            <button
              onClick={() => fetchBookings()}
              className="p-2 bg-[#2A2A2A] hover:bg-[#3A3A3A] transition-colors rounded border border-white/10"
              title="Aggiorna adesso"
              data-testid="manual-refresh-button"
            >
              <RefreshCw size={16} className="text-[#FFDA00]" />
            </button>
            <button
              onClick={handleLogout}
              className="btn-secondary flex items-center gap-2"
              data-testid="logout-button"
            >
              <LogOut size={18} />
              Esci
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Auto-refresh indicator */}
        <div className="mb-4 flex items-center gap-2 text-xs text-gray-400" data-testid="auto-refresh-indicator">
          <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          Aggiornamento automatico attivo (ogni 30 secondi)
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card text-center" data-testid="stat-total">
            <h3 className="text-3xl font-bold text-[#FFDA00] mb-2">{bookings.length}</h3>
            <p className="text-gray-400">Totale Prenotazioni</p>
          </div>
          <div className="card text-center" data-testid="stat-pending">
            <h3 className="text-3xl font-bold text-yellow-500 mb-2">
              {bookings.filter((b) => b.status === 'pending').length}
            </h3>
            <p className="text-gray-400">In Attesa</p>
          </div>
          <div className="card text-center" data-testid="stat-confirmed">
            <h3 className="text-3xl font-bold text-green-500 mb-2">
              {bookings.filter((b) => b.status === 'confirmed').length}
            </h3>
            <p className="text-gray-400">Confermate</p>
          </div>
          <div className="card text-center" data-testid="stat-cancelled">
            <h3 className="text-3xl font-bold text-red-500 mb-2">
              {bookings.filter((b) => b.status === 'cancelled').length}
            </h3>
            <p className="text-gray-400">Cancellate</p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-4 flex-wrap" data-testid="booking-filters">
          {[
            { key: 'all', label: 'Tutte' },
            { key: 'pending', label: 'In Attesa' },
            { key: 'confirmed', label: 'Confermate' },
            { key: 'cancelled', label: 'Cancellate' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 font-medium transition-all ${
                filter === f.key
                  ? 'bg-[#FFDA00] text-[#222222]'
                  : 'bg-[#2A2A2A] text-gray-400 hover:bg-[#3A3A3A]'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Bookings Table */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-400">Caricamento...</p>
          </div>
        ) : filteredBookings.length === 0 ? (
          <div className="card text-center py-12" data-testid="no-bookings">
            <p className="text-gray-400">Nessuna prenotazione trovata.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bookings-table">
              <thead>
                <tr className="bg-[#1A1A1A] border-b border-[#FFDA00]/30">
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Nome</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Contatto</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Data & Ora</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Ospiti</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Note</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Stato</th>
                  <th className="px-4 py-3 text-left text-[#FFDA00] font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredBookings.map((booking) => (
                  <tr
                    key={booking.booking_id}
                    className="border-b border-white/10 hover:bg-[#2A2A2A] transition-colors"
                    data-testid={`booking-row-${booking.booking_id}`}
                  >
                    <td className="px-4 py-3 text-white font-medium">{booking.name}</td>
                    <td className="px-4 py-3 text-gray-300">
                      <div className="text-sm">
                        <div className="flex items-center gap-1 mb-1">
                          <Mail size={12} className="text-[#FFDA00]" />
                          {booking.email}
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone size={12} className="text-[#FFDA00]" />
                          {booking.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      <div className="flex items-center gap-1 mb-1">
                        <Calendar size={14} className="text-[#FFDA00]" />
                        {booking.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock size={14} className="text-[#FFDA00]" />
                        {booking.time}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      <div className="flex items-center gap-1">
                        <Users size={14} className="text-[#FFDA00]" />
                        {booking.guests}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-300 text-sm max-w-xs truncate">
                      {booking.notes || '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${getStatusColor(booking.status)}`}>
                        {getStatusLabel(booking.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {booking.status !== 'confirmed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.booking_id, 'confirmed')}
                            className="p-2 bg-green-600 hover:bg-green-700 transition-colors rounded"
                            title="Conferma (invia email al cliente)"
                            data-testid={`confirm-booking-${booking.booking_id}`}
                          >
                            <Check size={16} />
                          </button>
                        )}
                        {booking.status !== 'cancelled' && (
                          <button
                            onClick={() => updateBookingStatus(booking.booking_id, 'cancelled')}
                            className="p-2 bg-red-600 hover:bg-red-700 transition-colors rounded"
                            title="Cancella (invia email al cliente)"
                            data-testid={`cancel-booking-${booking.booking_id}`}
                          >
                            <X size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => deleteBooking(booking.booking_id)}
                          className="p-2 bg-gray-600 hover:bg-gray-700 transition-colors rounded"
                          title="Elimina definitivamente (senza email)"
                          data-testid={`delete-booking-${booking.booking_id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
